/** The Deep's features: expanding-wall corridors, slime shelves, keyed vaults, clocks. */

import type { Rng } from '../../sim/rng';
import type { Plan } from './plan';
import { awayFromInbox, between, chance } from './plan';
import type { Sketch } from './sketch';
import { kingDistance } from './sketch';

/**
 * A brick-lined corridor of dirt and diamonds with an expanding wall at its far end: whatever
 * Digby digs out, the wall fills behind him. Grab and go.
 */
export function expandCorridor(sk: Sketch, rng: Rng, plan: Plan): boolean {
  const len = plan.bonus ? between(rng, 4, 7) : between(rng, 5, 10);
  const spot = sk.findSpot(rng, len + 3, 5);
  if (!spot) return false;
  sk.claim(spot);
  sk.fillRect(spot, '.');
  const x0 = spot.x + 1;
  const y = spot.y + 2;
  for (let i = 0; i <= len; i++) {
    sk.set(x0 + i, y - 1, 'w');
    sk.set(x0 + i, y + 1, 'w');
  }
  const west = rng.oneIn(2);
  for (let i = 0; i < len; i++) sk.set(west ? x0 + 1 + i : x0 + i, y, chance(rng, 60) ? 'd' : '.');
  sk.set(west ? x0 : x0 + len, y, 'x');
  return true;
}

/**
 * A slime shelf loaded with boulders and diamonds over an empty basin: they seep through one by
 * one. Sometimes a butterfly lives in the basin, waiting for the rain.
 */
export function slimeShelf(sk: Sketch, rng: Rng, plan: Plan): boolean {
  return slime(sk, rng, plan, chance(rng, 35));
}

/** A slime shelf that always has its butterfly: the Deep's one-screen fuse. */
export function slimeOverMoth(sk: Sketch, rng: Rng, plan: Plan): boolean {
  return slime(sk, rng, plan, true);
}

function slime(sk: Sketch, rng: Rng, plan: Plan, moth: boolean): boolean {
  const len = plan.bonus ? between(rng, 4, 6) : between(rng, 4, 8);
  const basin = moth ? 3 : 2;
  const spot = sk.findSpot(rng, len + 2, basin + 4, awayFromInbox(plan));
  if (!spot) return false;
  sk.claim(spot);
  sk.fillRect(spot, '.');
  const x0 = spot.x + 1;
  for (let i = 0; i < len; i++) {
    sk.set(x0 + i, spot.y + 1, chance(rng, 55) ? 'd' : 'r');
    sk.set(x0 + i, spot.y + 2, 's');
    for (let j = 0; j < basin; j++) sk.set(x0 + i, spot.y + 3 + j, ' ');
  }
  if (moth) {
    const c = { x: x0 + rng.int(len), y: spot.y + 2 + basin };
    sk.set(c.x, c.y, 'B');
    plan.creatures.push(c);
  }
  return true;
}

/**
 * A steel strongroom full of diamonds behind a coloured door. Its key goes somewhere else in
 * the cave (`placeKeys`, once every feature is down).
 */
export function vault(sk: Sketch, rng: Rng, plan: Plan): boolean {
  const taken = new Set(plan.keys.map((k) => k.color));
  const free = [0, 1, 2].filter((c) => !taken.has(c));
  const color = free[rng.int(free.length)];
  if (color === undefined) return false;
  const ow = plan.bonus ? 5 : between(rng, 5, 7);
  const oh = plan.bonus ? 4 : between(rng, 4, 5);
  const spot = sk.findSpot(rng, ow + 2, oh + 2);
  if (!spot) return false;
  sk.claim(spot);
  sk.fillRect(spot, '.');
  const r = { x: spot.x + 1, y: spot.y + 1, w: ow, h: oh };
  sk.outline(r, 'W');
  for (let y = r.y + 1; y < r.y + oh - 1; y++) {
    for (let x = r.x + 1; x < r.x + ow - 1; x++) sk.set(x, y, chance(rng, 65) ? 'd' : '.');
  }
  if (chance(rng, 30)) sk.set(r.x + 1 + rng.int(ow - 2), r.y + 1, 't');
  const door = { x: rng.oneIn(2) ? r.x : r.x + ow - 1, y: r.y + oh - 2 };
  sk.set(door.x, door.y, String(4 + color));
  plan.keys.push({ color, door });
  return true;
}

/** One to three clocks in open dirt: ten seconds each. */
export function clocks(sk: Sketch, rng: Rng, plan: Plan): boolean {
  const n = plan.bonus ? between(rng, 2, 3) : between(rng, 1, 3);
  let placed = 0;
  for (let i = 0; i < n; i++) {
    const c = sk.findCell(rng, '.');
    if (!c) continue;
    sk.set(c.x, c.y, 't');
    placed++;
  }
  return placed > 0;
}

/** Each vault's key, in open dirt away from its door. False if one found no home. */
export function placeKeys(sk: Sketch, rng: Rng, plan: Plan): boolean {
  for (const k of plan.keys) {
    const c = sk.findCell(rng, '.', (cell) => kingDistance(cell, k.door) >= 8);
    if (!c) return false;
    sk.set(c.x, c.y, String(1 + k.color));
    sk.claim({ x: c.x, y: c.y, w: 1, h: 1 });
  }
  return true;
}
